import { Fragment, useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Plus,
  Users,
  ChevronRight,
  CircleAlert,
  CircleMinus,
  TriangleAlert,
  FileClock,
  FileWarning,
} from '@/components/icons'
import { StatCard } from './ui/stat-card'
import { hapticLight } from '@/lib/haptics'
import { SearchInput } from './ui/search-input'
import { Card } from '@/components/ui/card'
import { ProfileAvatar } from './ProfileAvatar'
import { MenuItem } from './ui/menu-item'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PaxDrawer } from './PaxDrawer'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { usePassengerGetAll } from '../lib/hooks/passenger'
import {
  useDocumentGetExpiring,
  useDocumentGetExpired,
} from '../lib/hooks/document'
import { useIsAdmin } from '../hooks/useIsCompanyAdmin'
import { Button } from './ui/button'
import { getInitials } from '../lib/utils'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'

type PaxTab = 'active' | 'deleted'

interface PAXProps {
  initialSearch?: string
  basePath?: string
}

export function PAX({ initialSearch, basePath = '/operations/pax' }: PAXProps) {
  const navigate = useNavigate()
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? '')
  const [isAddPAXDrawerOpen, setIsAddPAXDrawerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<PaxTab>('active')

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate({
        to: basePath,
        search: { search: searchQuery || undefined } as any,
        replace: true,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  const { data: allPassengers = [], isPending: loading } = usePassengerGetAll({
    includeDeleted: true,
  })
  const { data: expiringDocs = [] } = useDocumentGetExpiring({
    category: 'Passenger',
  })
  const { data: expiredDocs = [] } = useDocumentGetExpired({
    category: 'Passenger',
  })

  const activePassengers = allPassengers.filter((p) => !p.isDeleted)
  const deletedPassengers = allPassengers.filter((p) => p.isDeleted)

  const expiringPaxIds = useMemo(
    () => new Set(expiringDocs.map((d) => d.passengerId).filter(Boolean)),
    [expiringDocs]
  )
  const expiredPaxIds = useMemo(
    () => new Set(expiredDocs.map((d) => d.passengerId).filter(Boolean)),
    [expiredDocs]
  )

  const filterBySearch = (list: typeof allPassengers) => {
    const searchLower = searchQuery.toLowerCase()
    return list.filter(
      (passenger) =>
        (passenger.firstName?.toLowerCase() || '').includes(searchLower) ||
        (passenger.lastName?.toLowerCase() || '').includes(searchLower) ||
        (passenger.email?.toLowerCase() || '').includes(searchLower) ||
        (passenger.role?.toLowerCase() || '').includes(searchLower)
    )
  }

  const filteredActive = filterBySearch(activePassengers)
  const filteredDeleted = filterBySearch(deletedPassengers)

  const expiredCount = activePassengers.filter((p) =>
    expiredPaxIds.has(p.id)
  ).length
  const expiringCount = activePassengers.filter(
    (p) => expiringPaxIds.has(p.id) && !expiredPaxIds.has(p.id)
  ).length

  const renderPaxListSkeleton = () => (
    <Card className="p-2">
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index}>
            <div className="flex items-center gap-3 px-3 py-2">
              <Skeleton className="size-8 rounded-full shrink-0" />
              <div className="flex flex-col gap-2 flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            {index < 5 && <div className="h-px w-full bg-border-L1" />}
          </div>
        ))}
      </div>
    </Card>
  )

  const renderEmpty = (message: string) => (
    <Card className="p-2">
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-border-3 mx-auto mb-3" />
        <p className="text-content-primary-subtle">{message}</p>
      </div>
    </Card>
  )

  const renderPaxList = (list: typeof allPassengers) => (
    <Card className="p-2">
      <div className="flex flex-col gap-2">
        {list.map((passenger, index) => {
          const fullName =
            `${passenger.firstName || ''} ${passenger.lastName || ''}`.trim()
          const initials = getInitials(passenger.firstName, passenger.lastName)

          const isDeleted = passenger.isDeleted
          const isExpired = !isDeleted && expiredPaxIds.has(passenger.id)
          const isExpiring = !isDeleted && expiringPaxIds.has(passenger.id)

          return (
            <Fragment key={passenger.id}>
              {index > 0 && <hr className="border-border-L1" />}
              <Link to={`${basePath}/${passenger.id}` as any}>
                <MenuItem
                  size="large"
                  leftIcon={
                    <ProfileAvatar
                      className="size-8"
                      photoS3Key={passenger.photoS3Key}
                      avatarColor={passenger.avatarColor}
                      initials={initials}
                      fallbackClassName="text-background"
                    />
                  }
                  secondaryText={passenger.role}
                  rightIcon={
                    <span className="flex items-center gap-2">
                      {isDeleted ? (
                        <CircleMinus className="size-5 text-content-primary-subtle" />
                      ) : isExpired ? (
                        <div className="flex items-center justify-center size-[25px] rounded-full bg-background-negative-subtle">
                          <TriangleAlert className="size-4 text-content-negative-bold" />
                        </div>
                      ) : isExpiring ? (
                        <div className="flex items-center justify-center size-[25px] rounded-full bg-background-notice-subtle">
                          <CircleAlert className="size-4 text-background-notice-bold" />
                        </div>
                      ) : null}
                      <ChevronRight className="size-5 text-content-primary-subtle" />
                    </span>
                  }
                >
                  <span
                    className={isDeleted ? 'text-content-primary-subtle' : ''}
                  >
                    {fullName || 'Unknown'}
                    {isDeleted && (
                      <span className="ml-2 text-xs text-content-primary-subtle font-normal">
                        (Inactive)
                      </span>
                    )}
                  </span>
                </MenuItem>
              </Link>
            </Fragment>
          )
        })}
      </div>
    </Card>
  )

  const isDesktop = useIsDesktop()

  const body = (
    <div className="flex flex-col gap-4 pt-4">
        {/* Sticky search band; the tab header pins right below it (top-11 = input height) */}
        <div className="sticky top-0 z-20 bg-surface-brand-L0">
          <SearchInput
            placeholder="Search passenger"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
        </div>

        {/* Stats Card */}
        {loading ? (
          <div className="flex w-full gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[88px] flex-1 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="flex w-full gap-3">
            <StatCard
              variant="default"
              className="flex-1 min-w-0"
              label="Total"
              value={activePassengers.length}
              icon={<Users />}
              caption="Active passengers"
            />
            <StatCard
              variant="default"
              className="flex-1 min-w-0"
              label="Expiring"
              value={expiringCount}
              icon={<FileClock />}
              caption="Next 30 days"
            />
            <StatCard
              variant="default"
              className="flex-1 min-w-0"
              label="Expired"
              value={expiredCount}
              icon={<FileWarning />}
              caption="Need renewal"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as PaxTab)}
          className="gap-0"
        >
          {/* Sticky tab header: opaque, full-width band so the list scrolls beneath it */}
          <div className="sticky top-11 z-10 bg-surface-brand-L0 pb-2">
            <TabsList className="w-full">
              <TabsTrigger value="active">
                Active ({activePassengers.length})
              </TabsTrigger>
              <TabsTrigger value="deleted">
                Deleted ({deletedPassengers.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="active" className="flex flex-col">
            {loading
              ? renderPaxListSkeleton()
              : filteredActive.length === 0
                ? renderEmpty('No passengers found')
                : renderPaxList(filteredActive)}
          </TabsContent>

          <TabsContent value="deleted" className="flex flex-col">
            {loading
              ? renderPaxListSkeleton()
              : filteredDeleted.length === 0
                ? renderEmpty('No deleted passengers')
                : renderPaxList(filteredDeleted)}
          </TabsContent>
        </Tabs>

        {/* Add PAX Drawer */}
        <PaxDrawer
          isOpen={isAddPAXDrawerOpen}
          onClose={() => setIsAddPAXDrawerOpen(false)}
        />
      </div>
  )

  const breadcrumb = (
    <span>
      <Link to="/operations" className="hover:underline">
        Operations
      </Link>
      <span className="text-content-primary-subtle"> / Passengers</span>
    </span>
  )

  const addAction =
    isAdminLoaded && isAdmin ? (
      <Button
        roundness="round"
        variant="brand"
        size="sm"
        className="gap-2"
        onClick={() => {
          hapticLight()
          setIsAddPAXDrawerOpen(true)
        }}
      >
        Add Passenger
        <Plus className="size-4 text-content-primary-white" />
      </Button>
    ) : undefined

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="PAX Directory"
          breadcrumb={breadcrumb}
          action={addAction}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title="PAX Directory"
          icon={
            isAdminLoaded && isAdmin ? (
              <Button roundness="round" variant="brand" size="sm" className="size-8 p-0">
                <Plus className="size-4 text-content-primary-white" />
              </Button>
            ) : undefined
          }
          onIconClick={
            isAdminLoaded && isAdmin
              ? () => {
                  hapticLight()
                  setIsAddPAXDrawerOpen(true)
                }
              : undefined
          }
        />
      }
      className="pb-4"
    >
      {body}
    </AppLayout>
  )
}
